<script setup lang="ts">
  import { computed } from 'vue'
  import { BaseButton } from '@qic/ui-components'
  import { useUserStore } from '@/shared/store'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import type { TravelerInformation as ApiTravelerInformation } from '@/shared/types/api.types'
  import FlowDataProtectReminder from '@/entities/Flow/components/FlowDataProtectReminder'
  import type { HolderInformation } from './components/TravelerCard/TravelerCard.types'
  import { getTravelersInformation } from './FlowInsuredTravelersWidget.utils'
  import {
    ADD_EVENT_NAME,
    DELETE_EVENT_NAME,
    EDIT_EVENT_NAME,
    HOLDER_ID,
    PROCEED_EVENT_NAME,
    TRAVELERS_LIMIT_OUTBOUND,
  } from './FlowInsuredTravelersWidget.constants'
  import TravelerCard from './components/TravelerCard/TravelerCard.vue'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useWinterSport } from '@/shared/composables'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'

  interface Props {
    travelers: ApiTravelerInformation[]
    typeOfTravel?: Exclude<TravelDirection, TravelDirection.UNKNOWN>
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: typeof EDIT_EVENT_NAME, id: string): void
    (e: typeof DELETE_EVENT_NAME, id: string): void
    (e: typeof ADD_EVENT_NAME): void
  }

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
  })

  const emit = defineEmits<Emits>()

  const userStore = useUserStore()
  const { withWinterSport } = useWinterSport()

  const isOutbound = computed(() => props.typeOfTravel === TravelDirection.OUTBOUND)

  const travelersInformation = computed(() =>
    getTravelersInformation(props.travelers, props.typeOfTravel, withWinterSport.value)
  )
  const limitTravelersOutBound = computed(
    () => isOutbound.value && travelersInformation.value.length + 1 >= TRAVELERS_LIMIT_OUTBOUND
  )
  const holderName = computed(() => `${userStore.firstName} ${userStore.familyName}`)

  const holderInformation = computed((): HolderInformation => {
    const generalInformation = {
      email: String(userStore.email || ''),
      phone: String(userStore.maskedPhone || ''),
      dateOfBirth: new Date(userStore.dateOfBirth || ''),
      nationality: String(userStore.nationality?.nationalityName),
      passportNumber: String(userStore.passportNumber || ''),
      gender: String(userStore.gender || ''),
    }

    const winterSportInformation = withWinterSport.value
      ? { withWinterSport: userStore.withWinterSport }
      : {}

    const outboundInformation = {
      emiratesId: String(userStore.emiratesId || ''),
      civilIdExpiryDate: new Date(userStore.civilIdExpiryDate || ''),
      ...winterSportInformation,
    }

    if (isOutbound.value) {
      return {
        ...generalInformation,
        ...outboundInformation,
      }
    } else {
      return generalInformation
    }
  })

  const onHolderEdit = () => {
    gaService.sendInsuredTravelersEdit(props.typeOfTravel)
    emit(EDIT_EVENT_NAME, HOLDER_ID)
  }

  const onTravelerDelete = (id: string) => {
    gaService.sendInsuredTravelersDelete(props.typeOfTravel)
    emit(DELETE_EVENT_NAME, id)
  }

  const onTravelerEdit = (id: string) => {
    gaService.sendInsuredTravelersEdit(props.typeOfTravel)
    emit(EDIT_EVENT_NAME, id)
  }

  const onAddTraveler = () => {
    gaService.sendInsuredTravelersAdd(props.typeOfTravel)
    emit(ADD_EVENT_NAME)
  }

  const gaService = useGoogleAnalytics()

  const onProceed = () => {
    gaService.sendInsuredTravelersProceed(
      props.typeOfTravel,
      travelersInformation.value.length + 1,
      userStore.destination?.map((country) => country?.id || '')
    )
    emit(PROCEED_EVENT_NAME)
  }
</script>

<template>
  <FlowCard :title="$t('travellers_insured')" with-button @button-click="onProceed">
    <div class="travelers">
      <div class="travelers__cards">
        <TravelerCard
          is-holder
          :name="holderName"
          :information="holderInformation"
          class="travelers__card"
          @edit="onHolderEdit"
        />
        <TravelerCard
          v-for="traveler in travelersInformation"
          :key="traveler.id"
          :name="traveler.name"
          :information="traveler.information"
          class="travelers__card"
          @delete="onTravelerDelete(traveler.id)"
          @edit="onTravelerEdit(traveler.id)"
        />
      </div>
      <div class="travelers__add-button">
        <BaseButton
          icon="plus"
          secondary
          medium
          :disabled="limitTravelersOutBound"
          @click="onAddTraveler"
        >
          {{ $t('inbound.travelers.add_button') }}
        </BaseButton>
      </div>
    </div>

    <template #after>
      <div class="travelers__after-content">
        <FlowDataProtectReminder />
      </div>
    </template>
  </FlowCard>
</template>

<style scoped lang="scss">
  @import './FlowInsuredTravelersWidget';
</style>
